import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { getGetBulkOrderQueryOptions } from "@/requests/gen/react-query/bulk-order";
import BulkOrderConfirmCard from "./-bulk-order-confirm-card";
import BulkOrderContentsCard from "./-bulk-order-contents-card";
import BulkOrderHistory from "./-bulk-order-history";
import { Separator } from "@/components/ui/separator";
import {
  AlertCircle,
  Building2,
  CreditCard,
  MapPin,
  Phone,
  Printer,
} from "lucide-react";
import {
  BulkOrderStatusEnum,
  PaymentTypeEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { BulkOrderCrumbs } from "../../-crumbs";
import BulkOrderActions from "./-bulk-order-actions";
import BulkOrderNoteForm from "./-bulk-order-notes-form";
import { BulkReorderCard } from "./-bulk-reorder-card";
import { RRule } from "rrule";
import { Button } from "@/components/ui/button";
import parsePhoneNumber from "libphonenumber-js";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_private/_practice-360/bulk-order/$bulkOrderId/",
)({
  loader: ({ context: { queryClient }, params: { bulkOrderId } }) =>
    queryClient.ensureQueryData(getGetBulkOrderQueryOptions(bulkOrderId)),
  component: BulkOrderReviewPage,
});

function BulkOrderReviewPage() {
  const { bulkOrderId } = Route.useParams();
  // Fetch the bulk_order data
  const bulk_order = useSuspenseQuery(
    suspenseOptions(getGetBulkOrderQueryOptions(bulkOrderId)),
  );

  const canEdit =
    bulk_order.data?.status === BulkOrderStatusEnum.Draft ||
    bulk_order.data?.status === BulkOrderStatusEnum.Reorder;

  const isCancelled = bulk_order.data?.status === BulkOrderStatusEnum.Cancelled;

  const differentSuborderAddresses = bulk_order.data?.bulk_suborders?.some(
    (suborder) =>
      suborder.ship_to_name !== bulk_order.data?.entity.name ||
      suborder.ship_to_st_address_1 !== bulk_order.data?.entity.st_address_1 ||
      suborder.ship_to_st_address_2 !== bulk_order.data?.entity.st_address_2 ||
      suborder.ship_to_city !== bulk_order.data?.entity.city ||
      suborder.ship_to_state_province !==
        bulk_order.data?.entity.state_province ||
      suborder.ship_to_postal_code !== bulk_order.data?.entity.postal_code,
  );

  const rrule = RRule.fromString(
    bulk_order.data?.bulk_reorder_schedule?.rrule ?? "",
  );

  return (
    <div>
      <title>{`Review Bulk Order ${bulkOrderId} - Legrande Health Portal`}</title>
      <BulkOrderCrumbs
        canEdit={canEdit}
        isReorder={bulk_order.data?.status === BulkOrderStatusEnum.Reorder}
      >
        {bulk_order.data && (
          <BulkOrderActions
            bulkOrder={bulk_order.data}
            refetch={bulk_order.refetch}
          />
        )}
      </BulkOrderCrumbs>

      {/* Main Content */}

      <main className="grid flex-1 items-start gap-4 xl:grid-cols-3">
        {/* Big Column */}
        <div className="grid gap-4 xl:col-span-2">
          {/* Contents Section */}
          <div className="relative">
            <BulkOrderContentsCard
              bulk_order={bulk_order.data}
              canEdit={canEdit}
              refetch={bulk_order.refetch}
            />
            {isCancelled && (
              <div className="absolute inset-0 bg-order-cancelled z-50 rounded-lg" />
            )}
          </div>

          {bulk_order.data?.status === BulkOrderStatusEnum.Reorder ? (
            <Card className="gap-2">
              <CardHeader>
                <CardTitle className="text-base">Schedule</CardTitle>
                <CardAction>
                  <Button size="sm" variant="link" asChild>
                    <Link
                      to="/bulk-reorder/$bulkOrderId/schedule"
                      params={{ bulkOrderId }}
                    >
                      Edit
                    </Link>
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent className="space-y-4">
                <BulkReorderCard bulk_order={bulk_order.data} rrule={rrule} />
              </CardContent>
            </Card>
          ) : (
            <Card className="gap-2">
              {/* History Section */}
              <CardHeader>
                <CardTitle className="text-base">History</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <BulkOrderHistory bulk_order={bulk_order.data} />
                {bulk_order.data && (
                  <BulkOrderNoteForm
                    bulk_order={bulk_order.data}
                    onSuccess={bulk_order.refetch}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="grid gap-4">
          {/* Confirm Section */}
          {bulk_order.data && (
            <div className="relative">
              {bulk_order.data && (
                <BulkOrderConfirmCard
                  bulk_order={bulk_order.data}
                  refetch={bulk_order.refetch}
                />
              )}
              {isCancelled && (
                <div className="absolute inset-0 bg-order-cancelled z-50 rounded-lg" />
              )}
            </div>
          )}
          <Card>
            <CardContent>
              {/* Shipping Information */}
              <div className="space-y-4 h-full">
                <Label>Shipping Information</Label>
                {differentSuborderAddresses && (
                  <Alert variant="warning">
                    <AlertDescription className="flex items-center text-sm">
                      <AlertCircle className="size-8" />
                      One or more suborders have shipping addresses that are
                      different than the address below.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex gap-2">
                    <Building2 className="size-4 text-muted-foreground mt-1" />
                    <span>{bulk_order.data?.entity.name}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="size-4 text-muted-foreground mt-1" />
                    <address className="not-italic text-muted-foreground">
                      <div>{bulk_order.data?.entity.st_address_1}</div>
                      {bulk_order.data?.entity.st_address_2 && (
                        <div>{bulk_order.data.entity.st_address_2}</div>
                      )}
                      <div>
                        {bulk_order.data?.entity.city},{" "}
                        {bulk_order.data?.entity.state_province}{" "}
                        {bulk_order.data?.entity.postal_code}
                      </div>
                    </address>
                  </div>
                  <div className="flex gap-2">
                    <Phone className="size-4 text-muted-foreground mt-1" />
                    <span className="text-muted-foreground">
                      {bulk_order.data?.entity.phone
                        ? (parsePhoneNumber(
                            bulk_order.data.entity.phone,
                          )?.formatNational() ?? "-")
                        : "-"}
                    </span>
                  </div>
                  {bulk_order.data?.entity.fax && (
                    <div className="flex gap-2">
                      <Printer className="size-4 text-muted-foreground mt-1" />
                      <span className="text-muted-foreground">
                        {parsePhoneNumber(
                          bulk_order.data.entity.fax,
                        )?.formatNational() ?? bulk_order.data.entity.fax}
                      </span>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Payment Information */}
                <div className="space-y-2">
                  <Label>Payment</Label>
                  <div className="gap-3 text-sm text-muted-foreground">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <CreditCard className="size-4" />

                        <span>
                          {bulk_order.data?.payment_type ==
                            PaymentTypeEnum.card &&
                            `${bulk_order.data?.payment?.brand} ending in
                            ${bulk_order.data?.payment?.masked_number.slice(-4)}`}

                          {bulk_order.data?.payment_type ==
                            PaymentTypeEnum.other && "Other"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
